/**
 * useLazyMount
 *
 * Returns a ref and a boolean `shouldMount` that becomes true once the element
 * approaches the viewport. Designed for deferred section rendering — children
 * are not mounted at all until the observer fires.
 *
 * Defaults to rootMargin '300px 0px' so sections begin mounting ~300 px before
 * the user scrolls to them, avoiding any visible pop-in.
 *
 * Fires once: once shouldMount is true it never goes back to false.
 *
 * Respects `prefers-reduced-motion`: shouldMount starts as true so content
 * renders immediately without any deferred behaviour.
 */

import { useRef, useState, useEffect } from 'react';

export interface LazyMountOptions {
  /** Margin around the root viewport used for early triggering. Default "300px 0px". */
  rootMargin?: string;
  /** Fraction of element visible before triggering. Default 0. */
  threshold?: number;
}

export interface LazyMountResult {
  ref: React.RefObject<HTMLDivElement>;
  shouldMount: boolean;
}

export function useLazyMount(options: LazyMountOptions = {}): LazyMountResult {
  const { rootMargin = '300px 0px', threshold = 0 } = options;

  const prefersReducedMotion =
    globalThis.window !== undefined &&
    globalThis.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const ref = useRef<HTMLDivElement>(null);
  const [shouldMount, setShouldMount] = useState(prefersReducedMotion);

  useEffect(() => {
    if (prefersReducedMotion) return;

    const node = ref.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldMount(true);
          observer.unobserve(node);
        }
      },
      { rootMargin, threshold }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [prefersReducedMotion, rootMargin, threshold]);

  return { ref, shouldMount };
}
